import type { IAccountOpeningApplicationForm, } from "@/modules/application/common/types";
import { currencyOptions } from "@/modules/application/helpers";
import {
  useCreateAccountOpeningApplicationMutation,
  useGetAccountOpeningApplicationQuery,
  useUpdateAccountOpeningApplicationMutation
} from "@/modules/application/services";
import { INPUT_MAX_LIMIT } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { ApplicationLayout, Select } from "@/shared/components";
import { useHasFormChanges } from "@/shared/hooks";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

import { ApplicationTermsAndConditionsLink } from "../ApplicationTermsAndConditionsLink";

export const AccountOpeningApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, watch, reset } = useFormProps();
  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const [
    createAccountOpeningApplicationApi,
    { isLoading: isCreateLoading }
  ] = useCreateAccountOpeningApplicationMutation();

  const [
    updateAccountOpeningApplicationApi,
    { isLoading: isUpdateLoading }
  ] = useUpdateAccountOpeningApplicationMutation();

  const {
    data: accountOpeningApplication,
    isLoading: isGetApplicationLoading,
  } = useGetAccountOpeningApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const accountOpeningApplicationAction = useCallback((values: IAccountOpeningApplicationForm) => {
    return isEdit ?
      updateAccountOpeningApplicationApi({
        data: values,
        id: `${accountOpeningApplication?.id}`
      }).unwrap() :
      createAccountOpeningApplicationApi({
        data: values,
        idempotencyKey,
      }).unwrap();
  }, [accountOpeningApplication]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      applicationNumber: isEdit ?
        accountOpeningApplication?.applicationNumber : values.applicationNumber,
      purpose: accountOpeningApplication?.purpose,
      currency: accountOpeningApplication?.currency,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    applicationNumber: values.applicationNumber,
    purpose: values.purpose,
    currency: values.currency,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!accountOpeningApplication?.id) {
      setDefaultValues();
    }
  }, [accountOpeningApplication]);

  return (
    <ApplicationLayout
      title={t("application.accountOpening.title")}
      page="Account opening application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={accountOpeningApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="currency"
                className="mb-32"
                errorMessageIcon="infoFilled"
                shouldValidate
                As={(props) => (
                  <Select
                    {...props}
                    required
                    options={currencyOptions}
                    label={t("application.accountOpening.currency")}
                    placeholder={t("application.accountOpening.chooseCurrency")}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-12"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <FormField
                name="isAgreeWithConditions"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isAgreeWithConditions}
                    label={<ApplicationTermsAndConditionsLink/>}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
